import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Sparkles, Building2 } from "lucide-react";
import TrustBadges from "./TrustBadges";
import FavoriteButton from "./FavoriteButton";

/**
 * ConciergeCard — homepage "Meet Your Experience Experts" tile.
 *
 * Positions the concierge (the person) as the product. Customers book the
 * expert; the concierge designs the complete experience. Specialties are
 * inspiration chips, not menu items.
 *
 * Card never shows a per-activity price. Pricing is "Custom Packages" or
 * "Starting at $X" to reinforce that experiences are bespoke.
 *
 * DEMO PROFILE MODE (`c.is_demo_profile === true`):
 *   - Renders an "Example Concierge Profile" pill at the top so the card
 *     never pretends to be a real verified human.
 *   - Hides all trust badges (Elite, Identity Verified, TREL Certified,
 *     TREL+). Those are reserved exclusively for real Concierges who
 *     earned them.
 *   - Card STILL routes to /concierge/{slug} so customers can review the
 *     example bio + portfolio. The public profile page detects demo status
 *     and shows the "Example Concierge Profile" banner + disables booking.
 */
export default function ConciergeCard({ c }) {
  const isDemo = c?.is_demo_profile === true;
  const isBusiness = c?.account_type === "business";
  // Compute a URL-safe slug from the concierge's display name. Must match the
  // backend `_slugify_name` so deep links work for both seeded and fallback
  // (Marcus / Sophia / David / Ava) concierges.
  const slug = slugify(c.name);
  // Every concierge card opens the public profile — even example/demo
  // profiles. The ConciergePublic page detects `is_demo` from the API/
  // fallback payload and shows the "Example Concierge Profile" banner +
  // disables booking. Customers and preview visitors need to evaluate the
  // concierge through a profile page before deciding to start planning.
  const href = `/concierge/${slug}`;
  const firstName = (c.name || "").split(" ")[0] || "this concierge";
  const ctaName = isBusiness ? c.name : firstName;
  const specs = (c.specializes || []).slice(0, 4);
  const starting = c.starting_at || c.price_floor;
  return (
    <Link
      to={href}
      data-testid={isDemo ? `concierge-card-demo-${c.user_id}` : `concierge-card-${c.user_id}`}
      className="group rounded-3xl overflow-hidden bg-[#FFF8EE] shadow-luxury hover:shadow-luxury-lg transition-all duration-300 hover:-translate-y-1 flex flex-col border border-[#071A2D]/5"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#071A2D]/5">
        <img
          src={c.photo}
          alt={c.name}
          loading="lazy"
          className="w-full h-full object-cover object-center group-hover:scale-[1.04] transition-transform duration-700"
        />
        {/* Subtle gradient so badges/labels always sit on a readable surface */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#071A2D]/35 to-transparent pointer-events-none"/>
        <div className="absolute top-3 left-3 right-14 max-w-full">
          {isDemo ? (
            <span
              data-testid={`concierge-card-demo-label-${c.user_id}`}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full bg-[#FFF8EE]/95 text-[#071A2D] shadow-sm"
            >
              <Sparkles size={11} className="text-[#C9A45C]" /> Example Concierge Profile
            </span>
          ) : (
            <TrustBadges provider={c} size="sm" />
          )}
        </div>
        {/* Heart save button — top-right corner. Demo profiles still render
            it for visual completeness; the button itself surfaces a
            "preview only" toast on click. */}
        <div className="absolute top-3 right-3">
          <FavoriteButton
            conciergeUserId={c.user_id}
            isDemo={isDemo}
            initialSaved={!!c._saved}
            size="sm"
            testid={`favorite-btn-card-${c.user_id}`}
          />
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {isBusiness && (
                <span
                  data-testid={`concierge-card-business-pill-${c.user_id}`}
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 rounded-md bg-[#064E3B]/8 text-[#064E3B] shrink-0"
                  title="Business Concierge"
                >
                  <Building2 size={9}/> Business
                </span>
              )}
            </div>
            <div className="font-serif text-2xl leading-tight text-[#071A2D] truncate mt-1">{c.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#064E3B] mt-1.5 font-semibold">{c.title}</div>
          </div>
          {!isDemo && (
            <div className="flex items-center gap-1 text-sm shrink-0">
              <Star size={14} fill="#C9A45C" color="#C9A45C" />
              <span className="font-semibold text-[#071A2D]">{Number(c.rating || 0).toFixed(1)}</span>
            </div>
          )}
        </div>

        {c.city && (
          <div className="mt-2.5 flex items-center gap-1.5 text-xs text-[#071A2D]/65">
            <MapPin size={12} className="shrink-0" /> {c.city}, {c.state}
            {!isDemo && c.moments_created ? <span className="ml-1">· {c.moments_created} moments created</span> : null}
          </div>
        )}

        {specs.length > 0 && (
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {specs.map((s) => (
              <span
                key={s}
                className="text-[11px] px-2.5 py-0.5 rounded-full bg-[#064E3B]/8 text-[#064E3B] font-medium"
              >
                {s}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-5 flex items-center justify-between border-t border-[#071A2D]/8">
          {isDemo ? (
            <>
              <span className="text-xs text-[#071A2D]/55 italic">Preview only</span>
              <span data-testid={`concierge-card-demo-cta-${c.user_id}`} className="text-xs font-semibold text-[#064E3B] group-hover:translate-x-0.5 transition-transform">
                Become a Concierge →
              </span>
            </>
          ) : (
            <>
              <span className="text-xs text-[#071A2D]/65">
                {starting ? <>Services from <span className="font-semibold text-[#071A2D]">${starting}</span></> : "Custom Quote"}
              </span>
              <span data-testid={`concierge-card-cta-${slug}`} className="text-xs font-semibold text-[#FF7A45] group-hover:translate-x-0.5 transition-transform">
                Work with {ctaName} →
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

// Slug helper — kept in sync with backend `_slugify_name` (server.py).
function slugify(name) {
  return (name || "")
    .toLowerCase()
    .split(/\s+/)
    .map((p) => p.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean)
    .join("-") || "concierge";
}
